/**
 * The undo pile of a versioned editor: snapshots of the draft, a cursor, and
 * the one rule that keeps a typed sentence from becoming forty entries
 * (issue #135).
 *
 * ## Snapshots, and why fifty of them cost far less than fifty drafts
 *
 * Every edition of this repository is written by SPREAD - `{ ...draft, name }`,
 * `[...steps.slice(0, i), next, ...steps.slice(i + 1)]` - so two consecutive
 * snapshots share every branch of the tree the change did not touch. What the
 * pile holds is therefore fifty references and the handful of objects each
 * write actually rebuilt, not fifty deep copies: the real cost sits far below
 * fifty times the size of a draft, which is what makes a cap of fifty a
 * generous one rather than a risky one.
 *
 * The alternative was a pile of PATCHES, and it was refused: a patch has to be
 * produced by whoever writes the draft, and the point of this lot is that no
 * call site of `onDraft` moves. A snapshot is the only entry a pile can build
 * from the outside.
 *
 * ## The coalescing window is what makes a sentence one entry
 *
 * A write landing less than `UNDO_COALESCE_MS` after the previous one REPLACES
 * the top of the pile instead of stacking on it. Typing pushes one write per
 * keystroke, so without this a single sentence would be forty undos, each one
 * giving back a character - which is not an undo anybody uses.
 *
 * The window SLIDES: each write pushes the clock forward, so a sentence typed
 * at a steady pace is one entry however long it is, and the pile grows at the
 * pauses. That is deliberate and it is the arbitrage this file took: a fixed
 * window from the first keystroke of a burst would have cut a long sentence
 * into halves at an arbitrary place, which reads as a bug rather than a rule.
 *
 * It is also what lets the caller stay ignorant. There are dozens of `onDraft`
 * call sites across four editors, and a pile that needed each of them to say
 * « this one is a keystroke, that one is a deletion » would be a lot that
 * touched every screen. The clock says it instead, imperfectly and everywhere.
 *
 * ## The seed can never be replaced
 *
 * `writtenAt` starts at `-Infinity`, so the FIRST write of a session is always
 * further than the window from its predecessor and always stacks. Without it,
 * a write landing in the first half second after the editor opened would have
 * overwritten the state the editor opened ON - and that state is the only one
 * nothing else can give back.
 *
 * ## Pure, and remounted rather than reset
 *
 * No React here: the editor holds one of these in state. A remount - which is
 * what « Recharger le brouillon du serveur » is, the route keying the editor on
 * the entity - therefore seeds a fresh pile, and there is no `clear()` to call
 * and forget.
 */

/**
 * How many snapshots the pile keeps, the oldest falling off the bottom.
 *
 * Fifty ENTRIES, so forty-nine undos: the entry at the bottom is the state to
 * come back TO, not a step. Nobody walks back fifty gestures; what a cap is
 * for is the session that never ends.
 */
export const UNDO_LIMIT = 50;

/**
 * Two writes closer than this are one entry.
 *
 * Half a second is above a fast typist's inter-key delay and below the pause
 * somebody makes before the next thing they meant to do, which is exactly the
 * boundary an undo should fall on.
 */
export const UNDO_COALESCE_MS = 500;

/**
 * The pile, and the position in it the screen is showing.
 *
 * `entries[cursor]` is on screen; everything before it is undo, everything
 * after it is redo. One array and an index rather than two stacks: a redo that
 * has to be rebuilt by popping one stack onto another is the shape where an
 * entry gets lost, and this one cannot lose what it never moves.
 */
export interface UndoStack<T> {
  readonly entries: readonly T[];
  readonly cursor: number;
  /** When `entries[cursor]` was written, for the coalescing window. */
  readonly writtenAt: number;
}

/** A pile seeded on the state the editor opened on. */
export function openUndoStack<T>(initial: T): UndoStack<T> {
  return { entries: [initial], cursor: 0, writtenAt: Number.NEGATIVE_INFINITY };
}

/**
 * One draft write: stacked, or coalesced onto the top when it is close enough.
 *
 * `at` is the caller's clock (`Date.now()`), passed in rather than read here so
 * the window is testable without faking time.
 *
 * Any write DROPS the redo: the branch somebody undid into is no longer the one
 * they are on, and keeping it would offer a redo that lands on a draft nothing
 * else in the pile leads to.
 */
export function recordUndo<T>(stack: UndoStack<T>, next: T, at: number): UndoStack<T> {
  const kept = stack.entries.slice(0, stack.cursor + 1);

  if (at - stack.writtenAt < UNDO_COALESCE_MS) {
    // Replace the top. `kept` always holds at least the seed, and the seed's
    // `-Infinity` keeps this branch out of reach until something has stacked.
    const coalesced = [...kept.slice(0, -1), next];
    return { entries: coalesced, cursor: coalesced.length - 1, writtenAt: at };
  }

  const grown = [...kept, next];
  // The cap drops from the BOTTOM: the oldest state is the one nobody is
  // walking back to, and dropping from the top would throw away the present.
  const entries = grown.length > UNDO_LIMIT ? grown.slice(grown.length - UNDO_LIMIT) : grown;
  return { entries, cursor: entries.length - 1, writtenAt: at };
}

export function canUndo<T>(stack: UndoStack<T>): boolean {
  return stack.cursor > 0;
}

export function canRedo<T>(stack: UndoStack<T>): boolean {
  return stack.cursor < stack.entries.length - 1;
}

/**
 * Step back one entry, or `null` when there is nothing behind the cursor.
 *
 * It answers the VALUE alongside the moved pile rather than leaving the caller
 * to index: `noUncheckedIndexedAccess` makes `entries[cursor]` a `T |
 * undefined` everywhere, and the only honest place to narrow it once is here.
 *
 * The clock is reset to `-Infinity` on the way out, so the next write stacks
 * rather than coalescing onto the entry the cursor has just landed on. Without
 * that, an undo followed immediately by a keystroke would eat the state it had
 * just given back.
 */
export function undoEntry<T>(stack: UndoStack<T>): { stack: UndoStack<T>; value: T } | null {
  if (!canUndo(stack)) return null;
  const value = stack.entries[stack.cursor - 1];
  // Unreachable after the guard above; it is what narrows the index access
  // without a non-null assertion, which Biome refuses in production code.
  if (value === undefined) return null;
  return {
    stack: { ...stack, cursor: stack.cursor - 1, writtenAt: Number.NEGATIVE_INFINITY },
    value,
  };
}

/** Step forward one entry, or `null` when the cursor is already on the last. */
export function redoEntry<T>(stack: UndoStack<T>): { stack: UndoStack<T>; value: T } | null {
  if (!canRedo(stack)) return null;
  const value = stack.entries[stack.cursor + 1];
  if (value === undefined) return null;
  return {
    stack: { ...stack, cursor: stack.cursor + 1, writtenAt: Number.NEGATIVE_INFINITY },
    value,
  };
}
